import type {
  EmergencyCardDocument,
  PdfArtifact,
  RecoveryCardDocument,
} from '../../application/models';
import type { PdfPort } from '../../application/ports';
import type { TestamentDocument } from '../../domain';
import { isPrintSanitizerReady, preloadPrintSanitizer, sanitizePrintHtml } from './print-sanitizer';
import { arePrintTemplatesReady, preloadPrintTemplates, printTemplates } from './print-templates';

export type PrintDocument = (html: string) => Promise<void>;
export type HtmlSanitizer = (html: string) => string;

export const PRINT_FRAME_MARKER = 'data-mt-print-frame';
const PRINT_FRAME_LOAD_TIMEOUT_MS = 10_000;

export class BrowserPrintError extends Error {
  constructor() {
    super('Browser print could not be started');
    this.name = 'BrowserPrintError';
  }
}

const removeStalePrintFrames = (): void => {
  for (const stale of document.querySelectorAll(`iframe[${PRINT_FRAME_MARKER}]`)) {
    stale.remove();
  }
};

const createPrintFrame = (html: string): HTMLIFrameElement => {
  const frame = document.createElement('iframe');
  frame.setAttribute(PRINT_FRAME_MARKER, '');
  frame.setAttribute('aria-hidden', 'true');
  frame.title = 'impression';
  frame.style.cssText =
    'position:fixed;inset-block-end:0;inset-inline-end:0;width:0;height:0;border:0;visibility:hidden;';
  // `srcdoc` keeps the doctype verbatim — `document.write` on a fresh window would too,
  // but srcdoc is a single atomic assignment with a reliable load event.
  frame.srcdoc = html;
  return frame;
};

const loadPrintFrame = (frame: HTMLIFrameElement): Promise<Window> =>
  new Promise<Window>((resolve, reject) => {
    const timer = setTimeout(() => reject(new BrowserPrintError()), PRINT_FRAME_LOAD_TIMEOUT_MS);
    frame.addEventListener(
      'load',
      () => {
        clearTimeout(timer);
        const target = frame.contentWindow;
        if (target === null || typeof target.print !== 'function') {
          reject(new BrowserPrintError());
          return;
        }
        resolve(target);
      },
      { once: true },
    );
    document.body.append(frame);
  });

/**
 * Primary strategy: a hidden same-origin iframe. No popup blocker to fight, and it is the
 * only path that still works in an iOS standalone PWA, where `window.open` is inert.
 */
export const printViaHiddenFrame: PrintDocument = async (html) => {
  removeStalePrintFrames();
  const frame = createPrintFrame(html);
  let target: Window;
  try {
    target = await loadPrintFrame(frame);
  } catch (error) {
    frame.remove();
    throw error;
  }
  // `print()` returns before the dialog is dismissed on Safari and on Chrome's async print
  // path: removing the frame right here cancels the job. Wait for `afterprint` instead —
  // and when it never fires (iOS), the next print call clears the stale frame.
  target.addEventListener('afterprint', () => frame.remove(), { once: true });
  target.focus();
  target.print();
};

/**
 * Fallback for engines that refuse to paginate an iframe. The feature string must NOT
 * contain `noopener`: per the HTML spec `window.open` then returns null and the document
 * can never be written. The popup is same-origin, so the opener link is severed here.
 */
export const printViaPopup: PrintDocument = async (html) => {
  const target = window.open('', '_blank');
  if (target === null) throw new BrowserPrintError();
  try {
    target.opener = null;
  } catch {
    // Not settable in every engine; the popup only ever holds our own sanitized markup.
  }
  target.document.open();
  target.document.write(html);
  target.document.close();
  // Same race as the frame path. If `afterprint` never fires the tab simply stays open for
  // the user to close, which beats cancelling their print job.
  target.addEventListener('afterprint', () => target.close(), { once: true });
  target.focus();
  target.print();
};

export const PRINT_HOST_MARKER = 'data-mt-print-host';

/**
 * On iOS every browser is WebKit — Safari, and Chrome too — and WebKit prints the top page
 * whichever frame asks: the hidden frame's `print()` showed the wasi's own page, « Les volontés
 * qui vous sont confiées », rather than the will (W8). An iPad says it is a Mac with a touch screen.
 */
export const printsTheTopPage = (
  agent: Pick<Navigator, 'userAgent' | 'platform' | 'maxTouchPoints'> = navigator,
): boolean =>
  /iPad|iPhone|iPod/u.test(agent.userAgent) ||
  (agent.platform === 'MacIntel' && agent.maxTouchPoints > 1);

const PAGE_RULE = /@page\s*\{[^}]*\}/gu;

/**
 * The print document's styles, for a shadow root: its page rule goes to the page itself, and
 * what it says of `html`, `:root` and `body` is said of the host and of the body's stand-in.
 */
const scopedPrintStyles = (css: string): string =>
  css
    .replace(PAGE_RULE, '')
    .replace(/(^|\})\s*(?:html|:root)\s*\{/gu, '$1\n:host {')
    .replace(/(^|\})\s*body\s*\{/gu, '$1\n.mt-print-body {');

const removeStalePrintHosts = (): void => {
  for (const stale of document.querySelectorAll(`[${PRINT_HOST_MARKER}]`)) stale.remove();
};

/**
 * iOS: the document is printed in the page itself. It goes into a host of its own, in a shadow
 * root so that its styles and the app's never meet; while printing, the page shows that host and
 * nothing else, on the document's own paper. Synchronous to the end, so the tap that asked is
 * still live when `print()` is called. The host stays hidden on screen until the next print, or
 * the next touch on the page once the print sheet is gone — `afterprint` is not to be trusted
 * there, and removing it too early would print a blank page.
 */
export const printInPlace: PrintDocument = async (html) => {
  removeStalePrintHosts();
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  const css = [...parsed.querySelectorAll('style')].map((style) => style.textContent).join('\n');
  const host = document.createElement('div');
  host.setAttribute(PRINT_HOST_MARKER, '');
  host.setAttribute('aria-hidden', 'true');
  const root = host.attachShadow({ mode: 'open' });
  const styles = document.createElement('style');
  styles.textContent = scopedPrintStyles(css);
  const body = document.createElement('div');
  body.className = 'mt-print-body';
  body.lang = parsed.documentElement.lang;
  body.dir = parsed.documentElement.dir;
  body.append(...[...parsed.body.childNodes].map((node) => document.importNode(node, true)));
  root.append(styles, body);

  const marked = `[${PRINT_HOST_MARKER}]`;
  const page = document.createElement('style');
  page.setAttribute(PRINT_HOST_MARKER, '');
  page.textContent =
    `${(css.match(PAGE_RULE) ?? []).join('\n')}\n` +
    `@media screen { div${marked} { display: none !important; } }\n` +
    '@media print {\n' +
    '  html, body { block-size: auto !important; min-block-size: 0 !important;' +
    ' overflow: visible !important; background: #fff !important; }\n' +
    `  body > :not(${marked}) { display: none !important; }\n` +
    `  div${marked} { display: block !important; }\n` +
    '}';
  document.head.append(page);
  document.body.append(host);

  // The sheet names the file after the page: the document's own title, while it is printed.
  const title = document.title;
  const printedTitle = parsed.title;
  if (printedTitle.length !== 0) document.title = printedTitle;
  const restoreTitle = (): void => {
    document.title = title;
  };
  window.addEventListener('afterprint', restoreTitle, { once: true });
  window.addEventListener(
    'pointerdown',
    () => {
      restoreTitle();
      removeStalePrintHosts();
    },
    { once: true },
  );
  window.print();
};

export const printInBrowser: PrintDocument = async (html) => {
  if (printsTheTopPage()) {
    await printInPlace(html);
    return;
  }
  try {
    await printViaHiddenFrame(html);
  } catch {
    await printViaPopup(html);
  }
};

export class BrowserPrintPdfAdapter implements PdfPort {
  constructor(
    private readonly printDocument: PrintDocument = printInBrowser,
    private readonly sanitize: HtmlSanitizer = sanitizePrintHtml,
    /** The scholar has not validated the religious content: the documents say so at their head. */
    private readonly unvalidatedContent = false,
  ) {}

  /**
   * The sanitizer and the templates, cleaned of their one asynchronous step before the document
   * is even built. When they are already in hand — which is the whole point of preloading them —
   * nothing is awaited here, and the tap that asked for the print reaches `print()` with the
   * gesture still live. iOS refuses to open the dialog otherwise.
   */
  private async ready(): Promise<void> {
    if (isPrintSanitizerReady() && arePrintTemplatesReady()) return;
    await Promise.all([preloadPrintSanitizer(), preloadPrintTemplates()]);
  }

  async render(document: TestamentDocument): Promise<PdfArtifact> {
    await this.ready();
    try {
      await this.printDocument(
        this.sanitize(
          printTemplates().renderBrowserPrintTemplate(document, this.unvalidatedContent),
        ),
      );
      return Object.freeze({
        bytes: new Uint8Array(),
        mediaType: 'application/pdf',
        suggestedFileName: 'testament.pdf',
      });
    } catch (error) {
      if (error instanceof BrowserPrintError) throw error;
      throw new BrowserPrintError();
    }
  }

  /** The page `render` would print, sanitized the same way, for the app to show first. */
  async preview(document: TestamentDocument): Promise<string> {
    await this.ready();
    return this.sanitize(
      printTemplates().renderBrowserPrintTemplate(document, this.unvalidatedContent),
    );
  }

  async renderEmergencyCard(document: EmergencyCardDocument): Promise<PdfArtifact> {
    await this.ready();
    try {
      await this.printDocument(
        this.sanitize(
          printTemplates().renderEmergencyCardPrintTemplate(document, this.unvalidatedContent),
        ),
      );
      return Object.freeze({
        bytes: new Uint8Array(),
        mediaType: 'application/pdf',
        suggestedFileName: 'carte-urgence.pdf',
      });
    } catch (error) {
      if (error instanceof BrowserPrintError) throw error;
      throw new BrowserPrintError();
    }
  }

  async renderRecoveryCard(document: RecoveryCardDocument): Promise<PdfArtifact> {
    await this.ready();
    try {
      await this.printDocument(
        this.sanitize(printTemplates().renderRecoveryCardPrintTemplate(document)),
      );
      return Object.freeze({
        bytes: new Uint8Array(),
        mediaType: 'application/pdf',
        suggestedFileName: 'carte-recuperation.pdf',
      });
    } catch (error) {
      if (error instanceof BrowserPrintError) throw error;
      throw new BrowserPrintError();
    }
  }
}
